import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import { CopyFineprint } from '../../atoms/Copytext';
import isClient from '~/helpers/isClient';
import SwiperWrapper from './partials/SwiperWrapper';
import SliderButton from './partials/SliderButton';
import vendorCSS from './partials/VendorCSS';
import useIsMobile from '~/hooks/useIsMobile';

const SliderContainer = styled.div`
  ${vendorCSS};
  width: 100%;
  height: ${(props) => (props.$narrow ? '305px' : 'auto')};

  .swiper {
    height: ${(props) => (props.$narrow ? '305px' : 'auto')};
  }

  .swiper-slide {
    margin-bottom: 0;
    box-sizing: border-box;
    height: ${(props) => (props.$narrow ? '250px' : '100%')};
  }

  .swiper-slide img {
    width: 100%;
    height: auto;
    margin-bottom: 45px;
    @media (${(props) => props.theme.slider.mobileSwitch}) {
      margin-bottom: 50px;
    }
  }
  .swiper-horizontal > .swiper-pagination-bullets {
    top: ${(props) =>
      props.$narrow ? 282 : Math.round((props.$swiperWidth * 9) / 16) + 23}px;
  }
  .swiper-horizontal > .swiper-pagination > .swiper-pagination-bullet-active {
    background: ${(props) =>
      props.$isDarkMode
        ? props.theme.color.gray400
        : props.theme.designTokens.colors.deepHumanBlue
            .deepHumanBlue100} !important;
  }
  .swiper-horizontal > .swiper-pagination > .swiper-pagination-bullet {
    background: ${(props) =>
      props.$isDarkMode
        ? props.theme.designTokens.colors.deepHumanBlue.deepHumanBlue30
        : props.theme.color.gray500};
  }
`;

const SlideIndex = styled(CopyFineprint)`
  margin: 0 20px;
  &::selection {
    color: ${(props) => props.theme.color.gray600};
    background-color: transparent;
  }
`;

const SliderControls = styled.div`
  position: absolute;
  top: ${(props) =>
    props.$narrow ? 270 : Math.round((props.$swiperWidth * 9) / 16) + 10}px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  z-index: 20;
  @media (${(props) => props.theme.slider.mobileSwitch}) {
    justify-content: space-between;
  }
`;

/**
 * @param {SliderProps} props
 * @returns {React.ReactElement | null} The rendered component
 */
const Slider = ({ content, className, narrow, isDarkMode }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [swiperWidth, setSwiperWidth] = useState(0);
  /** @type {import('react').MutableRefObject<import('swiper').Swiper | null>} */
  const swiperRef = useRef(null);
  const isMobile = useIsMobile({ contextKey: 'slider' });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isClient() && content.length > 0) {
      const updateSwiperWidth = () => {
        const width = get(swiperRef, 'current.width', 0);
        setSwiperWidth(width);
      };
      updateSwiperWidth();
      window.addEventListener('resize', updateSwiperWidth);

      // clean up eventListener
      return () => window.removeEventListener('resize', updateSwiperWidth);
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  /**
   * @param {import('swiper').Swiper} swiper
   * @returns {void}
   */
  const setSwiperRef = (swiper) => {
    swiperRef.current = swiper;
    setSwiperWidth(swiper?.width ?? 0);
  };

  /**
   * @param {import('swiper').Swiper} swiper
   * @returns {void}
   */
  const handleSlideChange = (swiper) => {
    setActiveSlide(swiper?.realIndex ?? 0);
  };

  /**
   * @param {React.MouseEvent} event
   * @param {SliderButtonDirection} direction
   * @returns {void}
   */
  const slideButtonHandler = (event, direction) => {
    if (swiperRef.current !== null) {
      switch (direction) {
        case 'prev':
          // TODO FRESEKAR-488: Unsafe usage of optional chaining. If it short-circuits with 'undefined' the evaluation will throw TypeError.eslintno-unsafe-optional-chaining
          // eslint-disable-next-line no-unsafe-optional-chaining
          (swiperRef?.current).slidePrev();
          break;
        case 'next':
          // TODO FRESEKAR-488: Unsafe usage of optional chaining. If it short-circuits with 'undefined' the evaluation will throw TypeError.eslintno-unsafe-optional-chaining
          // eslint-disable-next-line no-unsafe-optional-chaining
          (swiperRef?.current).slideNext();
      }
      event.stopPropagation();
    }
  };

  const swiperConfig = {
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    loop: true,
    on: {
      slideChange: handleSlideChange,
    },
  };

  if (isClient() && content.length > 0) {
    return (
      <SliderContainer
        className={className}
        $swiperWidth={swiperWidth}
        $narrow={narrow}
        $isDarkMode={isDarkMode}
      >
        <SwiperWrapper
          config={swiperConfig}
          content={content}
          onSwiper={setSwiperRef}
        />
        <SliderControls $swiperWidth={swiperWidth} $narrow={narrow}>
          {!isMobile && (
            <SlideIndex>
              {activeSlide + 1}/{content.length}
            </SlideIndex>
          )}
          <SliderButton direction="prev" handleClick={slideButtonHandler} />
          <SliderButton direction="next" handleClick={slideButtonHandler} />
        </SliderControls>
      </SliderContainer>
    );
  }
  return null;
};

export default Slider;
